// THE BOXES AN OFFER DECIDES — every "worked out for you" rule on the
// Realty forms, with the code that works it out.
//
// CONSUMER: shared (the binder, derive-rules.ts, only-what-you-know, and
// node-pool tests).
//
// ── WHY ONE TABLE ───────────────────────────────────────────────────
//
// The field atlas says, in a reviewer's sentence, which blanks are not
// anybody's to type: "checked when offer.possession is anything other
// than at COE", "offer.close_of_escrow_date → ... (month)", "rule: the
// side that prepares it". Until 2026-11-04 one of those sentences had
// code behind it (the Addenda Incorporated boxes) and the rest went out
// blank, were never on the sender's question list, and were counted as
// filled (derive-rules.ts has the history).
//
// This table is every such sentence the OS can now answer, each as ONE
// entry: the words that identify it (matched on the stored fill rule and
// the caption beside the box, never on a field id, because the same
// sentence appears on several forms and a row id is one form's), the
// value it prints for a given offer, and where that value came from, so
// the editor can say "your offer" / "the house" / "this package" / "this
// deal" on the box.
//
// ── TWO ANSWERS, AND THE DIFFERENCE MATTERS ─────────────────────────
//
// compute() returns a string to PRINT ("on" / "off" for a box, text for
// a blank) or null for "this send cannot answer it yet" — no close date
// typed, the house's sewer unknown, the package's forms not known. Null
// never means "off": an unticked box is a decided no, and this table
// says "off" when it has decided. A rule that is matched but returns
// null leaves the box exactly as the map had it (a firm default stays,
// an empty box stays empty) and the sender still sees the box as one the
// OS owns, because the rule IS implemented; it just has nothing to say
// on this offer.
//
// ── WHAT THIS TABLE REFUSES TO DO ───────────────────────────────────
//
// Nothing here reads a person's words. Every value comes from the TYPED
// term session 1 stored beside the words (offer-term-values.ts), the
// typed house facts (property-facts-source.ts), the package's form keys,
// or the side of the deal. If the typed value is missing the box is not
// guessed from the words; it stays blank and the sender sees why.
//
// Pure and dependency-free beyond the shared realty modules, so each
// rule is one testable function.

import {
  addendaBoxValue,
  addendumFormKeyForRule,
} from "./addenda-boxes";
import { BOLDSIGN_CHECKBOX_OFF, BOLDSIGN_CHECKBOX_ON } from "./field-types";
import type { EsignFieldType } from "./field-types";
import { brokerAgencyConfirmation } from "../realty/agent-side-resolution";
import { datePartsFromText } from "../realty/date-parts";
import type { PropertyFactsRead } from "../realty/property-facts-source";
import {
  earnestMoneyFrom,
  financingFrom,
  formatCents,
  type OfferTermTyped,
} from "../realty/offer-term-values";
import type { RealtySide } from "../realty/transaction-side";

/** Where a derived value came from. The word the sender reads on the
 *  box, so it must be one of the ghost-offer kinds the editor labels. */
export type DerivedSource = "offer" | "house" | "package" | "deal";

/** Everything a rule may read. Each half may be absent: a send with no
 *  offer still answers the house's boxes, and a Processing record with no
 *  facts still answers the offer's. */
export interface OfferDeriveContext {
  /** The offer's typed terms by slot key (offer-slots.ts). */
  typed: ReadonlyMap<string, OfferTermTyped>;
  /** The house, as the facts reader stored it. */
  facts: Partial<PropertyFactsRead> | null;
  /** The catalog form keys of every document in this envelope, or null
   *  when the caller does not know the package (a single-document apply
   *  with no package named). Null is "cannot answer", never "nothing". */
  packageFormKeys: ReadonlySet<string> | null;
  /** Buy or sell, when the record says. */
  side: RealtySide | null;
}

/** The box a rule is asked about: its stored rule text, the caption the
 *  map remembered beside it, its type and its seat. */
export interface DeriveBox {
  /** The fill rule's ref (the atlas's own words), or the whole rule. */
  ref: string;
  caption: string;
  fieldType: EsignFieldType;
  roleKey: string | null;
}

export interface OfferDeriveRule {
  /** The canonical rule name, registered in derive-rules.ts. */
  name: string;
  source: DerivedSource;
  /** What the box's panel row calls the value. */
  label: string;
  matches(box: DeriveBox): boolean;
  compute(ctx: OfferDeriveContext, box: DeriveBox): string | null;
}

const ON = BOLDSIGN_CHECKBOX_ON;
const OFF = BOLDSIGN_CHECKBOX_OFF;
const box = (on: boolean): string => (on ? ON : OFF);

/** Lower-cased, whitespace-collapsed, with the atlas's "line N:" prefix
 *  and ☐ glyphs removed, so a caption matches however the map spelled
 *  it. */
export function normalizeCaption(raw: string | null | undefined): string {
  return (raw ?? "")
    .replace(/[☐❑□]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    // "line 118: ADDRESS" and "line 97 Type of Financing: ..." lose their
    // line number; "lines 12 to 16: ..." keeps it (the word is "lines",
    // and the number is part of what identifies that run).
    .replace(/^line\s+[#\d~\-–]+\s*:?\s*/i, "")
    .toLowerCase();
}

function refOf(box: DeriveBox): string {
  return box.ref.replace(/\s+/g, " ").trim().toLowerCase();
}

// ─── Dates ─────────────────────────────────────────────────────────

/** The close-of-escrow date this offer carries, as ISO, or null when it
 *  is a duration whose anchor has not happened. */
function closeOfEscrowIso(ctx: OfferDeriveContext): string | null {
  const t = ctx.typed.get("close_of_escrow_date");
  if (!t) return null;
  if (t.kind === "date") return t.iso;
  if (t.kind === "duration") return t.computed_iso;
  return null;
}

const COE_PARTS: OfferDeriveRule = {
  name: "offer.close_of_escrow_date.parts",
  source: "offer",
  label: "Close of escrow",
  matches: (b) => refOf(b).startsWith("offer.close_of_escrow_date") && b.fieldType === "text",
  compute: (ctx, b) => {
    const iso = closeOfEscrowIso(ctx);
    const parts = iso ? datePartsFromText(iso) : null;
    if (!parts) return null;
    const ref = refOf(b);
    if (ref.includes("(month)")) return parts.month;
    if (ref.includes("(day)")) return parts.day;
    if (ref.includes("(two-digit year")) return parts.yearShort;
    // The Loan Status Update's "Close of Escrow Date": the whole date.
    return `${parts.month} ${parts.day}, ${parts.year}`;
  },
};

const EXPIRY_PARTS: OfferDeriveRule = {
  name: "offer.offer_expires.parts",
  source: "offer",
  label: "Offer expires",
  matches: (b) => refOf(b).startsWith("offer.offer_expires") && b.fieldType === "text",
  compute: (ctx, b) => {
    const t = ctx.typed.get("offer_expires");
    if (!t || t.kind !== "datetime") return null;
    const parts = datePartsFromText(t.date);
    if (!parts) return null;
    const ref = refOf(b);
    if (ref.includes("(month and day)")) return `${parts.month} ${parts.day}`;
    if (ref.includes("(year)")) return parts.year;
    if (ref.includes("(time of day)")) return t.time ? formatTimeOfDay(t.time) : null;
    return `${parts.month} ${parts.day}, ${parts.year}`;
  },
};

/** "17:00" → "5:00 p.m.", the way line 437 prints it. */
export function formatTimeOfDay(hhmm: string): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return null;
  const h24 = Number(m[1]);
  const minutes = m[2];
  if (h24 < 0 || h24 > 23) return null;
  const suffix = h24 < 12 ? "a.m." : "p.m.";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${minutes} ${suffix}`;
}

// ─── Possession ────────────────────────────────────────────────────

const POSSESSION_BOX: OfferDeriveRule = {
  name: "offer.possession.not_at_coe",
  source: "offer",
  label: "Possession",
  matches: (b) =>
    b.fieldType === "checkbox" && refOf(b).includes("offer.possession is anything other than at coe"),
  compute: (ctx) => {
    const t = ctx.typed.get("possession");
    if (!t || t.kind !== "possession") return null;
    return box(!t.at_coe);
  },
};

const POSSESSION_TERMS: OfferDeriveRule = {
  name: "offer.possession.alternate_terms",
  source: "offer",
  label: "Possession, alternate terms",
  matches: (b) => b.fieldType === "text" && refOf(b).startsWith("offer.possession"),
  compute: (ctx) => {
    const t = ctx.typed.get("possession");
    if (!t || t.kind !== "possession") return null;
    // At close of escrow: the alternate-terms line stays empty, which is
    // the form's own answer; nothing to print.
    return t.at_coe ? null : t.alternate_terms;
  },
};

// ─── Financing ─────────────────────────────────────────────────────

/** The loan-type word a line-97 box prints, read off its caption. */
function loanTypeInCaption(caption: string): string | null {
  const c = normalizeCaption(caption);
  const tail = c.split(":").pop()?.trim() ?? c;
  for (const word of ["conventional", "fha", "va", "usda", "assumption", "seller carryback", "other"]) {
    if (tail === word || tail.endsWith(` ${word}`) || tail.startsWith(`${word}`)) return word;
  }
  return null;
}

const LOAN_TYPE_BOXES: OfferDeriveRule = {
  name: "offer.loan_type.boxes",
  source: "offer",
  label: "Type of financing",
  matches: (b) =>
    b.fieldType === "checkbox" &&
    (refOf(b).startsWith("offer.loan_type") || refOf(b).includes("(also offer.loan_type)")) &&
    loanTypeInCaption(b.caption) !== null,
  compute: (ctx, b) => {
    const type = financingFrom(ctx.typed).loan_type;
    if (!type) return null;
    const want = loanTypeInCaption(b.caption);
    // A cash offer has no financing box on these forms: every one is a
    // decided no, and lines 12 to 16 say "cash" (FINANCING_LINES below).
    if (type === "Cash") return OFF;
    const spoken = type.toLowerCase();
    if (want === "other") return box(spoken === "other");
    return box(spoken === want);
  },
};

/** The dollar figure for a down payment, from the typed slots. */
function downPaymentCents(ctx: OfferDeriveContext): number | null {
  const fin = financingFrom(ctx.typed);
  const price = ctx.typed.get("purchase_price");
  if (fin.down_payment?.unit === "amount") return fin.down_payment.cents;
  if (price?.kind === "money" && fin.down_payment?.unit === "percent") {
    return Math.round((price.cents * fin.down_payment.basis_points) / 10_000);
  }
  if (price?.kind === "money" && fin.loan_amount_cents !== null && fin.loan_type !== "Cash") {
    return price.cents - fin.loan_amount_cents;
  }
  return null;
}

/** Lines 12 to 16 of the purchase contract ("Full Purchase Price, paid
 *  as outlined below"), composed from the financing terms:
 *  "$95,000 down; $380,000 conventional loan", or "$390,000 cash". */
export function financingLines(ctx: OfferDeriveContext): string | null {
  const fin = financingFrom(ctx.typed);
  const price = ctx.typed.get("purchase_price");
  if (!fin.loan_type) return null;
  if (fin.loan_type === "Cash") {
    return price?.kind === "money" ? `${formatCents(price.cents)} cash` : "Cash";
  }
  const parts: string[] = [];
  const down = downPaymentCents(ctx);
  if (down !== null) parts.push(`${formatCents(down)} down`);
  if (fin.loan_amount_cents !== null) {
    const noun = fin.loan_type === "Other" ? "loan" : `${fin.loan_type} loan`;
    parts.push(`${formatCents(fin.loan_amount_cents)} ${noun.replace(/^Conventional/, "conventional")}`);
  } else {
    parts.push(`${fin.loan_type} loan`);
  }
  return parts.join("; ");
}

const FINANCING_LINES: OfferDeriveRule = {
  name: "offer.financing.lines_12_to_16",
  source: "offer",
  label: "How the price is paid",
  // The atlas marks these five lines as the sender's own (fill=you, no
  // source), so the caption is the only handle. It is the purchase
  // contract's own wording and appears on no other form.
  matches: (b) =>
    b.fieldType === "text" && normalizeCaption(b.caption).startsWith("lines 12 to 16"),
  compute: (ctx) => financingLines(ctx),
};

// ─── Seller concessions ────────────────────────────────────────────

/** "5,000" — the number alone; the form prints its own "$". */
function plainDollars(cents: number): string {
  return formatCents(cents).replace(/^\$/, "");
}

/** "3" or "2.5" — the number alone; the form prints its own "%". */
function plainPercent(basisPoints: number): string {
  const pct = basisPoints / 100;
  return Number.isInteger(pct) ? pct.toFixed(0) : String(pct);
}

const CONCESSION_BOXES: OfferDeriveRule = {
  name: "offer.seller_concessions.boxes",
  source: "offer",
  label: "Seller concessions",
  matches: (b) => b.fieldType === "text" && refOf(b).includes("offer.seller_concession"),
  compute: (ctx, b) => {
    const t = ctx.typed.get("seller_concessions");
    if (!t || t.kind !== "money_or_percent") return null;
    const ref = refOf(b);
    if (ref.includes("= percent")) return t.unit === "percent" ? plainPercent(t.basis_points) : null;
    if (ref.includes("= amount")) return t.unit === "amount" ? plainDollars(t.cents) : null;
    return null;
  },
};

// ─── Inspection period ─────────────────────────────────────────────

const INSPECTION_DAYS: OfferDeriveRule = {
  name: "offer.inspection_period_days",
  source: "offer",
  label: "Inspection period",
  matches: (b) => b.fieldType === "text" && refOf(b).startsWith("offer.inspection_period_days"),
  // DECISION: the number prints even when it is the form's own ten. The
  // atlas reviewer left the blank empty at ten; the offer-writing plan
  // (section 6, Grant 2026-11-04) asks the preview to show "10" so Nate
  // can see the spoken term landed, and the audit's finding 10 was a
  // spoken seven losing to the printed ten. A visible number is the
  // safer side of that.
  compute: (ctx) => {
    const t = ctx.typed.get("inspection_period_days");
    return t?.kind === "integer_days" ? String(t.days) : null;
  },
};

// ─── Home warranty ─────────────────────────────────────────────────

function warranty(ctx: OfferDeriveContext): Extract<OfferTermTyped, { kind: "home_warranty" }> | null {
  const t = ctx.typed.get("home_warranty");
  return t?.kind === "home_warranty" ? t : null;
}

const WARRANTY_BOXES: OfferDeriveRule = {
  name: "offer.home_warranty.boxes",
  source: "offer",
  label: "Home warranty",
  matches: (b) => refOf(b).includes("offer.home_warranty"),
  compute: (ctx, b) => {
    const w = warranty(ctx);
    if (!w) return null;
    const ref = refOf(b);
    const cap = normalizeCaption(b.caption);
    const wanted = w.declined ? false : true;
    if (ref.includes("anything but declined or empty")) return box(wanted);
    if (ref.includes("is declined or empty")) return box(!wanted);
    if (b.fieldType === "checkbox" && ref.includes("(ordered by)")) {
      if (!w.ordered_by || w.declined) return w.declined ? OFF : null;
      return box(cap.includes(w.ordered_by));
    }
    if (b.fieldType === "checkbox" && ref.includes("(paid by)")) {
      if (w.declined) return OFF;
      if (!w.paid_by) return null;
      if (cap.includes("split")) return OFF;
      return box(cap.includes(w.paid_by));
    }
    if (b.fieldType === "text" && ref.includes("(cost cap)")) {
      return w.cap_cents !== null && !w.declined ? plainDollars(w.cap_cents) : null;
    }
    // The "optional coverage" / "issued by" blanks keep their TBD default
    // unless a plan or company was named (Nate, 2026-09-14).
    if (b.fieldType === "text" && ref.includes("home_warranty.coverage")) return w.plan;
    if (b.fieldType === "text" && ref.includes("home_warranty.company")) return w.company;
    return null;
  },
};

// ─── Personal property ─────────────────────────────────────────────

/** The four items the contract names with their own box. The first word
 *  of each pattern is what the caption says; the rest are what a person
 *  says. */
const NAMED_ITEMS: readonly (readonly [RegExp, RegExp])[] = [
  [/refrigerator/, /\b(refrigerator|fridge)\b/i],
  [/washer/, /\bwasher\b/i],
  [/dryer/, /\bdryer\b/i],
  [/spa|hot tub/, /\b(spa|hot tub|hottub)\b/i],
];

/** The items the offer includes, split the way a person lists them, with
 *  the four named ones taken out. */
export function extraPersonalProperty(text: string): string[] {
  return text
    .split(/,|;|\band\b|&|\n/i)
    .map((s) => s.replace(/^\s*(the|a|an)\s+/i, "").trim())
    .filter((s) => s.length > 0)
    .filter((s) => !NAMED_ITEMS.some(([, spoken]) => spoken.test(s)))
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1));
}

function personalProperty(ctx: OfferDeriveContext): string | null {
  const t = ctx.typed.get("personal_property_included");
  return t?.kind === "text" ? t.text : null;
}

const PERSONAL_PROPERTY_BOXES: OfferDeriveRule = {
  name: "offer.personal_property_included.boxes",
  source: "offer",
  label: "Personal property included",
  matches: (b) => refOf(b).includes("offer.personal_property_included"),
  compute: (ctx, b) => {
    const text = personalProperty(ctx);
    if (text === null) return null;
    const ref = refOf(b);
    const cap = normalizeCaption(b.caption);
    if (b.fieldType === "checkbox" && ref.includes("names it")) {
      const named = NAMED_ITEMS.find(([inCaption]) => inCaption.test(cap));
      if (!named) return null;
      return box(named[1].test(text));
    }
    const extras = extraPersonalProperty(text);
    if (ref.includes("beyond the four named")) return box(extras.length >= 1);
    if (ref.includes("second extra item")) return box(extras.length >= 2);
    if (ref.includes("(extra item 1)")) return extras[0] ?? null;
    if (ref.includes("(extra item 2)")) return extras[1] ?? null;
    return null;
  },
};

// ─── Earnest money's form and holder ───────────────────────────────

const EARNEST_MONEY_BOXES: OfferDeriveRule = {
  name: "offer.earnest_money.form_and_holder",
  source: "offer",
  label: "Earnest money",
  matches: (b) => {
    if (b.fieldType !== "checkbox") return false;
    const ref = refOf(b);
    return ref.includes("earnest money form") || ref.includes("deposited with escrow company");
  },
  compute: (ctx, b) => {
    const em = earnestMoneyFrom(ctx.typed);
    const ref = refOf(b);
    const cap = normalizeCaption(b.caption);
    if (ref.includes("earnest money form")) {
      // Only when the offer SAID how it is paid; otherwise the firm's
      // default (personal check) stands exactly as the map carries it.
      if (!em.form) return null;
      if (cap.includes("personal check")) return box(em.form === "personal_check");
      if (cap.includes("wire")) return box(em.form === "wire");
      if (cap.includes("other")) return box(em.form === "other");
      return null;
    }
    if (!em.held_by) return null;
    if (cap.includes("trust account")) return box(em.held_by === "broker_trust");
    if (cap.includes("escrow company")) return box(em.held_by === "escrow");
    return null;
  },
};

// ─── The package ───────────────────────────────────────────────────

const ADDENDA_BOXES: OfferDeriveRule = {
  name: "offer.package.forms",
  source: "package",
  label: "Addenda in this package",
  matches: (b) => b.fieldType === "checkbox" && addendumFormKeyForRule(b.ref) !== null,
  compute: (ctx, b) => (ctx.packageFormKeys ? addendaBoxValue(b.ref, ctx.packageFormKeys) : null),
};

// ─── The house ─────────────────────────────────────────────────────

const SEWER_BOXES: OfferDeriveRule = {
  name: "realty_property_facts.sewer_type.boxes",
  source: "house",
  label: "Sewer or septic",
  matches: (b) => b.fieldType === "checkbox" && refOf(b).startsWith("realty_property_facts.sewer_type"),
  compute: (ctx, b) => {
    const sewer = ctx.facts?.sewer_type ?? null;
    if (!sewer || sewer === "unknown") return null;
    const cap = normalizeCaption(b.caption);
    if (cap.includes("sewer system")) return box(sewer === "public_sewer");
    if (cap.includes("septic")) return box(sewer === "septic");
    if (cap.includes("alternative")) return box(sewer === "other");
    return null;
  },
};

const SOLAR_BOXES: OfferDeriveRule = {
  name: "realty_property_facts.solar_status.boxes",
  source: "house",
  label: "Solar",
  matches: (b) => b.fieldType === "checkbox" && refOf(b).startsWith("realty_property_facts.solar_status"),
  compute: (ctx, b) => {
    const solar = ctx.facts?.solar_status ?? null;
    if (!solar || solar === "unknown" || solar === "none") return null;
    const ref = refOf(b);
    if (ref.includes("= owned")) return box(solar === "owned");
    // The facts reader has no "utility owned" answer; that box is only
    // ever a decided no when the status is something else.
    if (ref.includes("= utility")) return OFF;
    if (ref.includes("leased") || ref.includes("financed")) return box(solar === "leased" || solar === "financed");
    return null;
  },
};

// ─── The side of the deal ──────────────────────────────────────────

/** Which party word a side-decided box names, read off the caption. */
function partyInCaption(caption: string): "seller" | "buyer" | "landlord" | "tenant" | "both" | null {
  const cap = normalizeCaption(caption);
  const tail = cap.split(/\bis\b|:/).pop()?.trim() ?? cap;
  if (/\bboth\b/.test(tail)) return "both";
  if (/\bseller/.test(tail)) return "seller";
  if (/\bbuyer/.test(tail)) return "buyer";
  if (/\blandlord/.test(tail)) return "landlord";
  if (/\btenant/.test(tail)) return "tenant";
  return null;
}

const SIDE_PREPARES: OfferDeriveRule = {
  name: "deal.side.prepared_by",
  source: "deal",
  label: "Which side this is",
  // The SCA's "originated by the ☐ Seller ☐ Buyer ☐ Landlord ☐ Tenant"
  // and the Agency Disclosure's "The undersigned ☐ Buyer(s) ☐ Seller(s)".
  matches: (b) =>
    b.fieldType === "checkbox" &&
    (refOf(b).startsWith("rule: the side that prepares it") || refOf(b).startsWith("rule: the record's side")),
  compute: (ctx, b) => {
    if (!ctx.side) return null;
    const party = partyInCaption(b.caption);
    if (!party) return null;
    const ours = ctx.side === "buy" ? "buyer" : "seller";
    // A sale, never a lease: landlord and tenant are decided no.
    return box(party === ours);
  },
};

const SIGNER_PARTY: OfferDeriveRule = {
  name: "transaction.people.signer_party",
  source: "deal",
  label: "Which party the signer is",
  matches: (b) =>
    b.fieldType === "checkbox" && refOf(b).startsWith("rule: the signer's casting on the transaction"),
  compute: (_ctx, b) => {
    const party = partyInCaption(b.caption);
    const seat = b.roleKey ?? "";
    if (!party) return null;
    if (/^buyer_\d+$/.test(seat)) return box(party === "buyer");
    if (/^seller_\d+$/.test(seat)) return box(party === "seller");
    return null;
  },
};

const AGENCY_CONFIRMATION: OfferDeriveRule = {
  name: "deal.side.agency_confirmation",
  source: "deal",
  label: "Agency confirmation",
  // Line 449 of the purchase contract: "Broker named in 8q is the agent
  // of ☐ Buyer ☐ Seller ☐ both". The atlas stores it as a policy default;
  // whom our broker represents follows from the side
  // (agent-side-resolution.ts), so the side decides and "both" (dual
  // agency) is never derived.
  matches: (b) =>
    b.fieldType === "checkbox" &&
    refOf(b).includes("agent of buyer") &&
    normalizeCaption(b.caption).includes("agency confirmation (8q)"),
  compute: (ctx, b) => {
    const represents = brokerAgencyConfirmation(ctx.side);
    if (!represents) return null;
    const party = partyInCaption(b.caption);
    if (party === "both") return OFF;
    if (party === "buyer" || party === "seller") return box(party === represents);
    return null;
  },
};

/** Every rule, in the order they are tried. A box matches at most one in
 *  practice; the first match wins so the order is only a tiebreak. */
export const OFFER_DERIVE_RULES: readonly OfferDeriveRule[] = [
  ADDENDA_BOXES,
  COE_PARTS,
  EXPIRY_PARTS,
  POSSESSION_BOX,
  POSSESSION_TERMS,
  LOAN_TYPE_BOXES,
  FINANCING_LINES,
  CONCESSION_BOXES,
  INSPECTION_DAYS,
  WARRANTY_BOXES,
  PERSONAL_PROPERTY_BOXES,
  EARNEST_MONEY_BOXES,
  SEWER_BOXES,
  SOLAR_BOXES,
  SIDE_PREPARES,
  SIGNER_PARTY,
  AGENCY_CONFIRMATION,
];

/** Every rule name this table implements, for derive-rules.ts. */
export const OFFER_DERIVE_RULE_NAMES: readonly string[] = OFFER_DERIVE_RULES.map((r) => r.name);

/** The rule a box is decided by, or null when no rule claims it. */
export function offerDeriveRuleFor(box: DeriveBox): OfferDeriveRule | null {
  // A box with no rule words and no caption is nobody's to decide.
  if (!box.ref.trim() && !box.caption.trim()) return null;
  for (const rule of OFFER_DERIVE_RULES) if (rule.matches(box)) return rule;
  return null;
}

// ─── The lead-paint initials pair (a placement, not a value) ───────

/** Which lead-paint initials block on page 4 of the purchase contract a
 *  box belongs to, from its caption: the "constructed prior to 1978"
 *  block or the "1978 or later" block. Null for every other box. */
export function leadPaintBlockOf(caption: string | null | undefined): "pre_1978" | "post_1978" | null {
  const cap = normalizeCaption(caption);
  if (!/\binitials\b/.test(cap)) return null;
  if (/prior to 1978/.test(cap)) return "pre_1978";
  if (/1978 or later/.test(cap)) return "post_1978";
  return null;
}

/** True when this box is a lead-paint initials line the house's year
 *  built says is NOT the one to sign. Null year: both stay, the sender
 *  chooses. */
export function leadPaintBlockDoesNotApply(
  caption: string | null | undefined,
  yearBuilt: number | null | undefined,
): boolean {
  const block = leadPaintBlockOf(caption);
  if (!block || yearBuilt === null || yearBuilt === undefined) return false;
  const pre = yearBuilt < 1978;
  return block === "pre_1978" ? !pre : pre;
}
